/**
 * 历史记录搜索
 * @param props
 * @constructor
 */
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import "./index.less";
import { Tabs, Modal, Button, message as toast, Radio, DatePicker } from "antd";
const { RangePicker } = DatePicker;
import { useTranslation } from "react-i18next";
import { connect } from "dva";
import ChatState from "@/types/chat/State";
import { ChatMessageType, MessageEmitBody, Session, SessionType } from "@/types/chat";
import SearchMessage from "./Message/comm";
import SearchFile from "./File";
import SearchMedia from "./Media";
import Filter from "./Filter";
import Context from "@/components/chat/context";
import { DrawTypeEnum } from "@/components/chat/enum";
import { analysisLog } from "@/utils/logAnalytics";
import { FocusSDK, getSafariVer, isFocusEnv } from "@/utils";
import ImService from "@/server/ImService";
import { uniqBy } from "lodash";
import { isChatPage, usePrevious } from "@/utils/chat/index";
import getCurVoiceHandler from "@/components/chat/handler/voiceHandler";
import { parseUidFromString } from "@jd/focus-desktop-comps/lib/es/utils";
import { parseUidFromSingleSessionId } from "@jd/jdee.im.sdk/lib/es/utils/utils";
import { convertEmployeeToString, isEqualUid } from "@/utils/chat/message";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import locale from "antd/es/date-picker/locale/zh_CN";
import { doSearchMessage, mapMessageData } from "./utils";
const { TabPane } = Tabs;

const LOAD_LIMIT = 20;

interface Props {
  selectedSession: Session;
  historySearchOpen: boolean;
  onClickClose?: () => void;
  updateSessionMessageMid: Function;
  updateSessionMessagesTimestamp: Function;
  setStartMessageId: Function;
}

type IProps = Readonly<Props>;
function MessageHistorySearch(props: IProps) {
  let {
    selectedSession,
    historySearchOpen,
    onClickClose,
    updateSessionMessageMid,
    updateSessionMessagesTimestamp,
    setStartMessageId
  } = props;
  const history = useHistory();
  const { currentEmployee, onToggleSetup } = React.useContext(Context);
  const { t } = useTranslation("chat");
  const [messages, setMessages] = useState<any>({});
  const refMessages = useRef({});
  const [currentTab, setCurrentTab] = useState("all");
  const imService = ImService.getInstance();
  const [searchParams, setSearchParams] = useState<any>(null);
  const preSessionId = usePrevious(selectedSession?.sessionId || "");
  const [exportLoading, setExportLoading] = useState(false);

  // 导出聊天记录
  const [exportVisible, setExportVisible] = useState(false);
  const [curDateRange, setCurDateRange] = useState("1");
  const [exportTimeRange, setExportTimeRange] = useState<any>([moment(), moment()]);
  const [dates, setDates] = useState<any>(exportTimeRange);

  useEffect(() => {
    if (
      getSafariVer() === 15 &&
      [SessionType.SINGLE, SessionType.SECRET_SINGLE, SessionType.GROUP].includes(
        selectedSession.sessionType
      )
    ) {
      const qlClipBoard: any = document.getElementsByClassName("ql-clipboard")[0];
      if (qlClipBoard) {
        qlClipBoard.style.display = historySearchOpen ? "none" : "";
      }
      const leftSide: any = document.getElementById("contentFrameLeftSide");
      if (leftSide) {
        leftSide.style.position = historySearchOpen ? "static" : "relative";
      }
      const msgBox: any = document.getElementById("message-box-container-scroll");
      if (msgBox) {
        msgBox.style.overflowY = historySearchOpen ? "hidden" : "overlay";
      }
    }
  }, [selectedSession.sessionType, historySearchOpen]);

  const getSearchTypes = (tab: string) => {
    if (tab === "card") {
      return {
        searchType: "",
        searchTypes: [ChatMessageType.TEMPLATE2, ChatMessageType.JOYSPACEFILE],
        incluedCard: true
      };
    } else if (tab === "file") {
      return {
        searchType: "",
        searchTypes: [ChatMessageType.FILE, ChatMessageType.EGOV_FILE]
      };
    } else if (tab === "all") {
      return {
        searchType: "",
        searchTypes: [
          ChatMessageType.IMAGE,
          ChatMessageType.FILE,
          ChatMessageType.EGOV_FILE,
          ChatMessageType.TEXT,
          ChatMessageType.VIDEO,
          ChatMessageType.VOICE,
          ChatMessageType.JOYSPACEFILE,
          ChatMessageType.TEMPLATE2
        ],
        incluedCard: true
      };
    } else if (tab === "media") {
      return {
        searchType: "",
        searchTypes: [ChatMessageType.IMAGE, ChatMessageType.VIDEO],
        incluedCard: false
      };
    }
    const type = tab;
    return {
      searchType: type,
      incluedCard: tab === "all"
    };
  };

  // 清空数据
  const clearMessageData = useCallback((resetTab = false) => {
    if (resetTab) {
      setCurrentTab("all");
    }
    refMessages.current = {};
    setMessages({});
  }, []);

  // 搜索
  const searchMessage = useCallback(
    (tab: string) => {
      // 加载中
      refMessages.current = {
        ...refMessages.current,
        [tab]: {
          ...(refMessages.current as any)[tab],
          isLoading: true
        }
      };
      setMessages(refMessages.current);
      imService
        .search({
          sessionId: selectedSession.sessionId,
          searchKey: "",
          ...getSearchTypes(tab),
          // composeVideo: tab === "media",
          desc: true,
          includeBurnAfterRead: true,
          limit: LOAD_LIMIT,
          ...searchParams
        })
        .then((res) => {
          const raws = (refMessages.current as any)[tab]?.dataSource || [];
          refMessages.current = {
            ...refMessages.current,
            [tab]: {
              dataSource: uniqBy([...(res as MessageEmitBody[]), ...raws], "id").sort(
                (a: any, b: any) => b.mid - a.mid
              ),
              isLoading: false,
              hasMore: res.length >= LOAD_LIMIT
            }
          };
          setMessages(refMessages.current);
        })
        .catch((e) => {
          console.log("searchMessage:>>>err", e);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSession.sessionId, searchParams]
  );

  // 加载更多
  const handleLoadMore = () => {
    let lastMid = -1;
    // 加载中
    refMessages.current = {
      ...refMessages.current,
      [currentTab]: {
        ...(refMessages.current as any)[currentTab],
        isLoading: true
      }
    };
    lastMid = ((refMessages.current as any)[currentTab]?.dataSource || []).slice(-1)[0]?.mid || -1;
    const lastTimestamp =
      ((refMessages.current as any)[currentTab]?.dataSource || []).slice(-1)[0]?.timestamp || 0;
    setMessages(refMessages.current);
    imService
      .search({
        startMid: lastMid,
        sessionId: selectedSession.sessionId,
        searchKey: "",
        ...getSearchTypes(currentTab),
        // composeVideo: currentTab === "media",
        desc: true,
        includeBurnAfterRead: true,
        limit: LOAD_LIMIT,
        ...searchParams,
        findTimestamp: lastTimestamp
      })
      .then((res) => {
        const raws = (refMessages.current as any)[currentTab]?.dataSource || [];
        refMessages.current = {
          ...refMessages.current,
          [currentTab]: {
            dataSource: [...raws, ...(res as MessageEmitBody[])],
            isLoading: false,
            hasMore: res.length >= LOAD_LIMIT
          }
        };
        setMessages(refMessages.current);
      })
      .catch((e) => {
        console.log("handleLoadMore:>>>err", e);
      });
  };

  // 搜索条件
  const hanldeFilterChange = useCallback(
    (val: any) => {
      setSearchParams({ ...searchParams, ...val });
      clearMessageData();
    },
    [searchParams, clearMessageData]
  );

  const handleJump = (item: any) => {
    const isSingle =
      item.sessionId.indexOf(`:${(window as any)?._BTS_CONFIG?.REACT_APP_DD_APPID}:`) !== -1
        ? true
        : false;
    const secret = item.sessionId.indexOf(":secret") !== -1 ? "&secret=true" : "";
    let url = "";
    const mid = item.fmid || item.mid;
    if (isChatPage()) {
      if (mid > -1) {
        setStartMessageId({ sMid: mid, sText: item.content });
      }
      // onClickClose?.();
      handleClickClose();
      return;
    }
    if (isSingle) {
      const [user1, user2] = parseUidFromSingleSessionId(item.sessionId);
      let userId = "";
      let app = "";
      let teamId = "";
      if (isEqualUid(user1, parseUidFromString(convertEmployeeToString(currentEmployee)))) {
        userId = user2.pin;
        app = user2.app;
        teamId = user2.teamId;
      } else {
        userId = user1.pin;
        app = user1.app;
        teamId = user1.teamId;
      }
      url = `/messages/s/${userId}?app=${app}&tid=${teamId}${secret}&top=${true}&sMid=${mid}&sText=${
        item.content || ""
      }&sTimestamp=${item.timestamp}`;
    } else {
      url = `/messages/g/${item.sessionId}?sMid=${mid}&top=${true}&sText=${
        item.content || ""
      }&sTimestamp=${item.timestamp}`;
    }
    history.push(url);
    // onClickClose?.();
    handleClickClose();
  };

  const isRobot = useMemo(() => {
    return selectedSession.uid?.pin?.startsWith("robot_");
  }, [selectedSession]);

  // 关闭浮层
  const handleClickClose = useCallback(() => {
    if (onClickClose) {
      onClickClose();
      return;
    }
    if (onToggleSetup) onToggleSetup(DrawTypeEnum.search);
  }, [onClickClose, onToggleSetup]);

  const handleRadioChange = (e: any) => {
    setCurDateRange(e.target.value);
    switch (e.target.value) {
      case "1":
        setExportTimeRange([moment(), moment()]);
        break;
      case "7":
        setExportTimeRange([moment().subtract(6, "days"), moment()]);
        break;
      case "30":
        setExportTimeRange([moment().subtract(29, "days"), moment()]);
        break;
      default:
        // setExportTimeRange(exportTimeRange);
        break;
    }
  };

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    if (dates) {
      const now = moment().format("YYYY-MM-DD");
      if (dateStrings[1] === now) {
        if (dateStrings[0] === now) {
          setExportTimeRange([moment(), moment()]);
          setCurDateRange("1");
          return;
        }
        if (dateStrings[0] === moment().subtract(6, "days").format("YYYY-MM-DD")) {
          setExportTimeRange([moment().subtract(6, "days"), moment()]);
          setCurDateRange("7");
          return;
        }
        if (dateStrings[0] === moment().subtract(29, "days").format("YYYY-MM-DD")) {
          setExportTimeRange([moment().subtract(29, "days"), moment()]);
          setCurDateRange("30");
          return;
        }
        const tempDate = moment(moment().subtract(29, "days").format("YYYY-MM-DD"));
        const date0 = moment(dateStrings[0]);
        if (date0.isBefore(tempDate)) {
          toast.info("单次至多支持导出30天的聊天记录, 请重新选择时间范围");
          setExportTimeRange(null);
          setCurDateRange("0");
        } else {
          setExportTimeRange([dates[0], moment()]);
          setCurDateRange("0");
        }
      } else {
        const minDate = moment(moment(dateStrings[1]).subtract(29, "days").format("YYYY-MM-DD"));
        if (moment(dateStrings[0]).isBefore(minDate)) {
          toast.info("单次至多支持导出30天的聊天记录, 请重新选择时间范围");
          setExportTimeRange(null);
        } else {
          setExportTimeRange([dates[0], dates[1]]);
        }
        setCurDateRange("0");
      }
    } else {
      setExportTimeRange(dates);
      setCurDateRange("0");
      return;
    }
  };

  const disabledDateFn = (current: any) => {
    const maxDate = current && current > moment().endOf("day");
    if (!dates) {
      return maxDate;
    }
    const tooLate = dates[0] && current.diff(dates[0], "days") > 29;
    const tooEarly = dates[1] && dates[1].diff(current, "days") > 29;
    return !!tooEarly || !!tooLate || maxDate;
  };

  const generateExcel = async (data: any) => {
    const workbook = utils.book_new();
    const worksheet = utils.aoa_to_sheet(data);
    utils.book_append_sheet(workbook, worksheet, "sheet1");
    const workbookout = write(workbook, { bookType: "xlsx", bookSST: true, type: "array" });
    try {
      saveAs(
        new Blob([workbookout], { type: "application/octet-stream" }),
        `${t("message_record")}-${selectedSession?.info?.name || ""}.xlsx`
      );
    } catch (e) {
      if (typeof console !== "undefined") console.log(e, workbookout);
    }
  };

  const handleExportMessages = async () => {
    setExportLoading(true);
    const startTime = exportTimeRange[0].startOf("day").format("x");
    const endTime = exportTimeRange[1].endOf("day").format("x");
    const param: any = {
      startTime: startTime,
      endTime: endTime
    };
    if (selectedSession.isGroup) {
      param.gid = selectedSession.id || selectedSession.sessionId || selectedSession.gid;
    } else {
      param.to = selectedSession.uid;
    }
    // 根据会话获取漫游消息且存到本地
    const res = await imService.getHistoryMessage(param);
    if (res) {
      // 待漫游结束后导出数据到本地
      const params = {
        sessionId: selectedSession.sessionId,
        searchKey: "",
        ...getSearchTypes("all"),
        desc: true,
        includeBurnAfterRead: true,
        limit: 500,
        startTimestamp: startTime,
        endTimestamp: endTime
      };
      if (isFocusEnv()) {
        FocusSDK.sendIpcMessage(
          "focus:im:export-messages-search",
          JSON.stringify({
            params,
            fileType: "excel",
            fileName: `${t("message_record")}-${selectedSession?.info?.name || ""}`
          })
        );
      } else {
        const msgs = await doSearchMessage([], params);
        if (!msgs?.length) {
          toast.info("未获取到任何聊天数据，无法导出");
          setExportLoading(false);
          return;
        }
        const uniqMsgs = uniqBy(msgs, "id");
        const rst = mapMessageData(uniqMsgs as any[]);
        await generateExcel(rst);
      }
      // setTimeout(() => {
      setExportLoading(false);
      // }, 2000);
    }
  };

  const modalName = useMemo(() => {
    if (historySearchOpen) {
      return selectedSession?.name || selectedSession?.info?.name;
    }
    return "";
  }, [historySearchOpen]);

  // 浮层header
  const modalHeader = useMemo(() => {
    const name = selectedSession?.name || selectedSession?.info?.name;
    if (currentTab === "all" && !selectedSession.isSecret) {
      return (
        <div className="modal-header-wrap">
          <span>{modalName}</span>
          {/* {isFocusEnv() && ( */}
          {/** 233隐藏导出 */}
          {/* <Button
            className="filter-btn"
            type="primary"
            onClick={() => {
              setExportVisible(true);
              setCurDateRange("1");
              setExportTimeRange([moment(), moment()]);
              setDates([moment(), moment()]);
            }}
          >
            导 出
          </Button> */}
          {/* )} */}
        </div>
      );
    } else {
      return <div className="modal-header-wrap">{name}</div>;
    }
  }, [
    currentTab,
    modalName,
    selectedSession.info.name,
    selectedSession.isSecret,
    selectedSession.name
  ]);

  useEffect(() => {
    if (historySearchOpen) {
      searchMessage(currentTab);
    }
  }, [historySearchOpen, searchMessage, searchParams, currentTab]);

  // 处理关闭
  useEffect(() => {
    if (!historySearchOpen) {
      const voiceHandler = getCurVoiceHandler();
      if (voiceHandler?.voiceAmr?.isPlaying) {
        voiceHandler?.voiceAmr?.stop();
      }
    }
  }, [historySearchOpen]);

  useEffect(() => {
    if (selectedSession?.sessionId !== preSessionId) {
      clearMessageData(true);
      setSearchParams(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession.sessionId, clearMessageData, preSessionId]);
  return (
    <>
      <Modal
        className="search-record-modal"
        title={modalHeader}
        width="712px"
        mask={true}
        // maskStyle={{
        //   opacity: "0.5"
        // }}
        destroyOnClose={false}
        closable={true}
        onCancel={handleClickClose}
        zIndex={99}
        footer={null}
        visible={historySearchOpen}
      >
        <Filter searchParams={searchParams} onChange={(val: any) => hanldeFilterChange(val)} />
        <Tabs
          defaultActiveKey="all"
          className="search-record-tabs"
          onTabClick={(key) => {
            analysisLog("Xtbg_Msg_Search", key);
            setCurrentTab(key);
          }}
          activeKey={currentTab}
        >
          <TabPane tab={t("message_all")} key="all">
            <SearchMessage
              currentSession={selectedSession}
              onClickClose={handleClickClose}
              handleJump={handleJump}
              currentEmployee={currentEmployee}
              {...messages["all"]}
              loadMore={handleLoadMore}
              searchFilter={searchParams}
              mark={currentTab === "all"}
              canJump={currentTab === "all"}
              currentTab={currentTab}
              setStartMessageId={setStartMessageId}
            />
          </TabPane>
          {!isRobot && (
            <>
              <TabPane tab={t("message_attachment")} key="file">
                <SearchFile
                  currentSession={selectedSession}
                  handleJump={handleJump}
                  currentEmployee={currentEmployee}
                  {...messages["file"]}
                  loadMore={handleLoadMore}
                  mark={currentTab === "all"}
                  historySearchOpen={historySearchOpen}
                />
              </TabPane>
              <TabPane tab={t("message_image_video")} key="media">
                <SearchMedia
                  currentSession={selectedSession}
                  updateSessionMessageMid={updateSessionMessageMid}
                  updateSessionMessagesTimestamp={updateSessionMessagesTimestamp}
                  {...messages["media"]}
                  loadMore={handleLoadMore}
                  mark={currentTab === "all"}
                />
              </TabPane>
              <TabPane tab={t("message_audio")} key="voice">
                <SearchMessage
                  currentSession={selectedSession}
                  onClickClose={handleClickClose}
                  currentEmployee={currentEmployee}
                  {...messages["voice"]}
                  loadMore={handleLoadMore}
                  mark={currentTab === "all"}
                />
              </TabPane>
              <TabPane tab={t("message_card")} key="card">
                <SearchMessage
                  currentSession={selectedSession}
                  onClickClose={handleClickClose}
                  currentEmployee={currentEmployee}
                  {...messages["card"]}
                  loadMore={handleLoadMore}
                  mark={currentTab === "all"}
                />
              </TabPane>
            </>
          )}
        </Tabs>
      </Modal>
      <Modal
        visible={exportVisible}
        title="导出聊天记录"
        width={600}
        okText={t("button.ok")}
        cancelText={t("button.cancel")}
        wrapClassName="exportMessagesModal"
        centered={true}
        destroyOnClose={true}
        confirmLoading={exportLoading}
        onCancel={() => setExportVisible(false)}
        onOk={() => {
          if (!exportTimeRange) {
            toast.info("您还未选择时间范围");
            return;
          }
          handleExportMessages();
        }}
      >
        <div className="export-messages-container">
          <div className="range-text">
            请选择要导出的聊天记录时间范围<span>（单次至多支持导出30天的聊天记录）</span>
          </div>
          <div className="range-radio">
            <Radio.Group onChange={handleRadioChange} value={curDateRange}>
              <Radio value="1">今天</Radio>
              <Radio value="7">最近7天</Radio>
              <Radio value="30">最近30天</Radio>
              <Radio value="0">自定义</Radio>
            </Radio.Group>
          </div>
          <div className="range-picker">
            <RangePicker
              locale={locale}
              value={exportTimeRange}
              onChange={handleDateRangeChange}
              onCalendarChange={(dates) => setDates(dates)}
              disabledDate={(current) => disabledDateFn(current)}
            />
          </div>
          <div className="range-tips">*提示：导出可能需要一些时间，请耐心等待</div>
        </div>
      </Modal>
    </>
  );
}
function mapStateToProps({ chat }: any) {
  const { selectedSession } = chat as ChatState;
  return { selectedSession };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { sendResult: any; sessionId: string }) {
      return dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    },
    updateSessionMessagesTimestamp(data: { timestamp: number }) {
      dispatch({ type: "chat/updateSessionMessagesTimestamp", payload: data });
    },
    setStartMessageId(data: { sMid: number; sText: string }) {
      dispatch({ type: "chat/setStartMessageId", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MessageHistorySearch);
